import React, { useEffect, useRef } from 'react';
import { Button, SparkIcon } from '@spark';
import ConstraintRow from './ConstraintRow';
import { buildEmptyConstraint } from './constraintOptions';

/**
 * Constraint rows list + "Add Constraint" button + a feedback banner
 * (Figma 3106-9825): blue info banner for 1-2 constraints, yellow warning
 * banner for more than 2. Shared by the creation wizard Step 1 and the
 * Task Settings modal.
 */
const ConstraintsEditor = ({
    constraints = [],
    onChange,
    isNerTask = false,
    entityTypeOptions = [],
    errors = null,
}) => {
    const showWarning = constraints.length > 2;
    const showInfo = constraints.length > 0 && constraints.length <= 2;
    const rootRef = useRef(null);
    const focusNewRow = useRef(false);

    const updateRow = (idx, next) => {
        const copy = [...constraints];
        copy[idx] = next;
        onChange(copy);
    };

    const removeRow = (idx) => onChange(constraints.filter((_, i) => i !== idx));

    const addRow = () => {
        focusNewRow.current = true;
        onChange([...constraints, buildEmptyConstraint()]);
    };

    // Rows render before the "Add Constraint" button, so a keyboard user who
    // just activated it would have to Shift+Tab back to reach the new fields.
    // Move focus into the new row's first field instead.
    useEffect(() => {
        if (!focusNewRow.current) return;
        focusNewRow.current = false;
        const rows = rootRef.current?.querySelectorAll('.cw-constraint__item');
        const lastRow = rows?.length ? rows[rows.length - 1] : null;
        lastRow
            ?.querySelector('button, input, select, textarea, [tabindex]:not([tabindex="-1"])')
            ?.focus();
    }, [constraints.length]);

    return (
        <div className="cw-constraints" ref={rootRef}>
            {showWarning && (
                <div className="cw-constraints__warning" role="alert">
                    <SparkIcon slug="circle-alert-triangle" size="medium" />
                    <span>
                        Warning: Highly restrictive constraints may limit the platform’s ability to deliver the best results.
                    </span>
                </div>
            )}

            {showInfo && (
                <div className="cw-constraints__info" role="status">
                    <SparkIcon slug="circle-info" size="medium" />
                    <span>
                        Know that a restrictive constraint may limit the platform’s ability to deliver the best results.
                    </span>
                </div>
            )}

            {constraints.map((constraint, idx) => (
                <ConstraintRow
                    key={constraint.id}
                    constraint={constraint}
                    onChange={(next) => updateRow(idx, next)}
                    onRemove={() => removeRow(idx)}
                    isNerTask={isNerTask}
                    entityTypeOptions={entityTypeOptions}
                    allConstraints={constraints}
                    error={errors?.[constraint.id] || null}
                />
            ))}

            <Button
                variant="tertiary"
                size="medium"
                startIcon={<SparkIcon slug="plus" size="medium" />}
                onClick={addRow}
                className="cw-constraints__add"
            >
                Add Constraint
            </Button>
        </div>
    );
};

export default ConstraintsEditor;
